import * as React from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const AI_GRADIENT =
  'bg-gradient-to-br from-fuchsia-500 to-violet-500 text-white shadow-sm'

const AI_GRADIENT_HOVER =
  'hover:from-fuchsia-600 hover:to-violet-600 hover:text-white'

const AI_ICON_SUBTLE = 'text-fuchsia-500 dark:text-fuchsia-400'

type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>

export type AiSparkleTone = 'solid' | 'subtle'

export type AiSparkleButtonProps = Omit<ButtonProps, 'variant'> & {
  iconClassName?: string
  tone?: AiSparkleTone
}

export const AiSparkleButton = React.forwardRef<
  HTMLButtonElement,
  AiSparkleButtonProps
>(({ className, iconClassName, size = 'icon-sm', tone = 'solid', children, ...props }, ref) => {
  const isSubtle = tone === 'subtle'
  return (
    <Button
      ref={ref}
      type={props.type ?? 'button'}
      variant="ghost"
      size={size}
      className={cn(
        'rounded-md',
        isSubtle
          ? 'hover:bg-fuchsia-500/10 dark:hover:bg-fuchsia-500/15'
          : cn(AI_GRADIENT, AI_GRADIENT_HOVER),
        className,
      )}
      {...props}
    >
      <Sparkles
        className={cn('size-3.5', isSubtle ? AI_ICON_SUBTLE : '', iconClassName)}
      />
      {children}
    </Button>
  )
})
AiSparkleButton.displayName = 'AiSparkleButton'

export type AiSparkleBadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  iconClassName?: string
}

export const AiSparkleBadge = React.forwardRef<
  HTMLSpanElement,
  AiSparkleBadgeProps
>(({ className, iconClassName, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'inline-flex size-7 items-center justify-center rounded-md',
      AI_GRADIENT,
      className,
    )}
    {...props}
  >
    <Sparkles className={cn('size-4', iconClassName)} />
  </span>
))
AiSparkleBadge.displayName = 'AiSparkleBadge'
